/**
 * AddressVerificationTile — the conditional Address Verification capture,
 * relocated INTO the Address & Contact section beside the Physical/Mailing
 * fields it corroborates (Bill 2026-06-11, LOCKED mockup v6 — it is NOT in
 * the top "Documents to capture" grid any more).
 *
 * Behavior (03-DETERMINISTIC-TILES-DECISION §3 / §3.1 — the reactive
 * "corroborated yet?" rule via `deriveAddressCorroboration`):
 *   - appears only while NO captured doc corroborates the STATED address
 *   - a matching DL suppresses it (the DL IS the proof)
 *   - recomputed on every capture AND every address-field edit (derived state)
 *
 * REQUIRED escalation (absorbed from the retired Stage-4 grid flip,
 * PdwStage4DocsGrid v0.14.x): when the non-corroboration came from a
 * captured-ID address CONFLICT and the admin toggle
 * `cip.addressVerification.requireOnMismatch` (SystemSettings; default ON)
 * is set, the tile renders REQUIRED (blocking semantics — the cart-commit
 * gate remains the backstop). Toggle off → tile still shows but stays
 * non-blocking (CONDITIONAL).
 *
 * Cross-lane contract: tileId / route key stays 'address-proof'
 * (DOC_TYPE_OPTIONS value → canonical docCode ADDR_PROOF), so existing
 * captures + extracts keep resolving. The tiles lane removed 'address-proof'
 * from cohortDocTiles' shared optional set; this component is now the ONLY
 * Address Verification capture surface.
 */

import { useCallback, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { Pill } from '@/components/primitives';
import { documentIconUrl } from '@/icons/flat-library';
import type { RaoPerson } from '../../../types';
import {
  PerDocScanModal,
  type CaptureArtifact,
  type ScanResultPayload,
} from '../PerDocScanModal';
import { makeScanHandler } from '../capture/scanRouter';
import { type AttachedDoc, estimateBase64Size, readAttachedDocs } from '../shared/attachedDocs';
import { tileIdToCanonicalDocCode } from './captureRouting';
import { deriveAddressCorroboration } from './addressCorroboration';

/** Stable cross-lane ids — do not rename (existing captures must resolve). */
const ADDRESS_PROOF_TILE_ID = 'address-proof';
const ADDRESS_PROOF_DOC_CODE = tileIdToCanonicalDocCode(ADDRESS_PROOF_TILE_ID); // 'ADDR_PROOF'

/**
 * Admin gate — `cip.addressVerification.requireOnMismatch` (SystemSettings).
 * Defaults TRUE per Bill's capture-pattern mockup (required-deferrable when an
 * ID-address mismatch is detected). Same key the retired Stage-4 flip read —
 * this tile is now its single living consumer.
 */
function readAddressMismatchGate(systemSettings: Record<string, unknown> | undefined): boolean {
  if (!systemSettings) return true;
  const raw = systemSettings['cip.addressVerification.requireOnMismatch'];
  if (raw === false || raw === 0 || raw === 'false' || raw === '0') return false;
  return true;
}

export interface AddressVerificationTileProps {
  /** The LIVE draft — corroboration re-derives from it every render. */
  person: RaoPerson;
  /**
   * Deep-merge patch applier (extra/extracts-aware) — the scan pipeline's
   * empty-fields-only patch lands through this (auto-fills Street/City/State/
   * Zip when the stated address is empty).
   */
  applyPatch: (patch: Partial<RaoPerson>) => void;
  /** Persists the captured proof onto `person.extra.attachedDocs`. */
  onDocumentCaptured: (doc: AttachedDoc) => void;
  readOnly?: boolean;
}

export function AddressVerificationTile({
  person,
  applyPatch,
  onDocumentCaptured,
  readOnly = false,
}: AddressVerificationTileProps): JSX.Element | null {
  const apiClient = useApiClient();
  const { config } = useConfig();
  const [scanOpen, setScanOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);

  const mismatchGate = useMemo(
    () => readAddressMismatchGate(config.systemSettings as Record<string, unknown> | undefined),
    [config.systemSettings],
  );

  // §3.1 — derived per render: re-evaluates on every capture + address edit.
  const corroboration = useMemo(() => deriveAddressCorroboration(person), [person]);

  const capturedProof = useMemo(
    () => readAttachedDocs(person).find((d) => d.docCode === ADDRESS_PROOF_DOC_CODE),
    [person],
  );

  const scanHandler = useMemo(
    () => makeScanHandler({ apiClient, person, applyPatch }),
    [apiClient, person, applyPatch],
  );

  const handleCapture = useCallback(
    (base64: string, mimeType: string): Promise<ScanResultPayload> =>
      scanHandler(ADDRESS_PROOF_TILE_ID, base64, mimeType),
    [scanHandler],
  );

  const handleApply = useCallback(
    (result: ScanResultPayload, artifact: CaptureArtifact): void => {
      setScanOpen(false);
      setDroppedFile(null);
      if (!result.success) return;
      onDocumentCaptured({
        docCode: ADDRESS_PROOF_DOC_CODE,
        source: 'cip-scan',
        filename: artifact.originalFilename ?? `${ADDRESS_PROOF_TILE_ID}-${Date.now()}.pdf`,
        contentType: artifact.pdfMimeType,
        sizeBytes: estimateBase64Size(artifact.pdfBase64),
        imageDataUrl: '',
        fileUrl: '',
        capturedAt: new Date().toISOString(),
      });
    },
    [onDocumentCaptured],
  );

  // Corroborated → the tile is suppressed; a one-line green note explains why
  // (so the FSR sees the rule worked rather than wondering where the tile went).
  if (corroboration.corroborated) {
    return (
      <div
        style={{
          fontSize: 12,
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--ok-bg, #f0f8ee)',
          border: '1px solid var(--ok-line)',
          color: 'var(--ink-700)',
          alignSelf: 'start',
        }}
      >
        <strong style={{ color: 'var(--pcb-green, #386732)' }}>Address corroborated ✓</strong>
        {' '}— the captured {corroboration.corroboratedBy ?? 'document'} matches the stated
        physical address, so no separate Address Verification document is needed.
        Editing the address away from the corroborated value brings the capture tile back.
      </div>
    );
  }

  const escalatedRequired = mismatchGate && !!corroboration.idAddressConflict;
  const iconUrl = documentIconUrl('pcb-address-verification') ?? documentIconUrl('address-proof');
  const captured = !!capturedProof;

  const statusNote = corroboration.idAddressConflict
    ? `The captured ${corroboration.idAddressConflict.docLabel}'s address differs from the stated address (${corroboration.idAddressConflict.detail}).`
    : corroboration.noStatedAddress
      ? 'No stated address entered yet — capture a proof document or enter the address to run the check.'
      : 'No captured document confirms the stated physical address yet.';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignSelf: 'start' }}>
      <button
        type="button"
        className={`pdw-docs-tile ${captured ? 'pdw-docs-tile--captured' : 'pdw-docs-tile--idle'}${!captured && !escalatedRequired ? ' pdw-docs-tile--optional' : ''}`}
        onClick={() => {
          if (readOnly) return;
          setDroppedFile(null);
          setScanOpen(true);
        }}
        onDragOver={(e) => {
          if (readOnly) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDrop={(e) => {
          if (readOnly) return;
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) {
            setDroppedFile(f);
            setScanOpen(true);
          }
        }}
        aria-label={`Address Verification — ${captured ? 'captured, click to re-scan' : 'click or drop a file'}`}
        title={captured ? 'Captured — click to re-scan / replace' : 'Click or drop a file'}
        style={{ minHeight: 0 }}
      >
        <div className="pdw-docs-tile__head">
          <div className="pdw-docs-tile__icon" aria-hidden>
            {iconUrl ? (
              <img src={iconUrl} alt="" width={40} height={40} />
            ) : (
              <span className="pdw-docs-tile__icon-fallback">A</span>
            )}
          </div>
          <div className="pdw-docs-tile__pill-slot">
            {escalatedRequired ? (
              <Pill variant="err">REQUIRED</Pill>
            ) : (
              <Pill variant="warn">CONDITIONAL</Pill>
            )}
            {captured && <Pill variant="ok">✓ Captured</Pill>}
          </div>
        </div>
        <div className="pdw-docs-tile__label">Address Verification</div>
        {captured && capturedProof?.filename && (
          <div className="pdw-docs-tile__filename" title={capturedProof.filename}>
            <span className="pdw-docs-tile__filename-icon" aria-hidden>📄</span>
            <span className="pdw-docs-tile__filename-text">{capturedProof.filename}</span>
          </div>
        )}
        <div className="pdw-docs-tile__hint">
          Utility bill / lease / mortgage or bank statement / W-2 / paystub — verifies the
          stated physical address.
        </div>
        <div className="pdw-docs-tile__status">
          {readOnly ? 'Read-only' : captured ? 'Click to re-scan' : 'Click or drop a file'}
        </div>
      </button>

      <div style={{ fontSize: 11.5, lineHeight: 1.45 }}>
        <strong style={{ color: escalatedRequired ? 'var(--pcb-red, #b10505)' : 'var(--pcb-amber-text, #8a6500)' }}>
          {escalatedRequired ? 'Verification required' : 'Not corroborated yet'}
        </strong>{' '}
        <span style={{ color: 'var(--ink-600)' }}>
          — {statusNote} A captured document whose address matches (e.g. a DL) suppresses
          this tile automatically.
        </span>
      </div>

      {scanOpen && (
        <PerDocScanModal
          open={scanOpen}
          docLabel="Address Verification"
          docHint="Utility bill / lease / mortgage statement / bank statement / W-2 / paystub — the extracted address auto-fills empty Contact fields and corroborates the stated address."
          iconSlug="pcb-address-verification"
          initialFile={droppedFile}
          onCapture={handleCapture}
          onApply={handleApply}
          onClose={() => {
            setScanOpen(false);
            setDroppedFile(null);
          }}
        />
      )}
    </div>
  );
}
